import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api.js'
import { CATEGORY_BY_SLUG, CATEGORIES, isInCategory, categoriesOf } from '../data/site.js'
import FinishGrid from '../components/FinishGrid.jsx'
import ProductCard from '../components/ProductCard.jsx'
import Icon from '../components/Icon.jsx'

// Two subcategories are the same chip when they only differ by case, spacing or a
// trailing plural — the owner types these by hand on each product.
const subKey = (s) => String(s || '').trim().toLowerCase().replace(/s$/, '')

// case-insensitive match across everything a customer might type
const matchesQuery = (p, q) =>
  [p.name, p.sku, p.subcategory, p.short_desc, ...categoriesOf(p)]
    .some((field) => String(field || '').toLowerCase().includes(q))

export default function Shop() {
  const { category } = useParams()
  const [params, setParams] = useSearchParams()
  const q = params.get('q') || ''
  const [products, setProducts] = useState(null)
  const [sub, setSub] = useState('')
  const [sortBy, setSortBy] = useState('featured')
  const active = category ? CATEGORY_BY_SLUG[category] : null

  const setQuery = (value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value) next.set('q', value)
      else next.delete('q')
      return next
    }, { replace: true })
  }

  useEffect(() => {
    api.products().then((d) => setProducts(d.products)).catch(() => setProducts([]))
  }, [])
  useEffect(() => setSub(''), [category])

  // the chip rows scroll sideways on a phone — keep the selected chip in view, without
  // scrolling the page itself (scrollLeft rather than scrollIntoView)
  const chipRows = useRef([])
  useEffect(() => {
    chipRows.current.forEach((row) => {
      const active = row?.querySelector('.chip-active')
      if (row && active) row.scrollLeft = Math.max(0, active.offsetLeft - 20)
    })
  }, [category, sub])

  const filtered = useMemo(() => {
    if (!products) return []
    let list = products
    if (active) list = list.filter((p) => isInCategory(p, category))
    if (sub) list = list.filter((p) => subKey(p.subcategory) === subKey(sub))
    const query = q.trim().toLowerCase()
    if (query) list = list.filter((p) => matchesQuery(p, query))
    if (sortBy === 'price-asc') list = [...list].sort((a, b) => (a.min_price ?? a.price ?? 0) - (b.min_price ?? b.price ?? 0))
    if (sortBy === 'price-desc') list = [...list].sort((a, b) => (b.min_price ?? b.price ?? 0) - (a.min_price ?? a.price ?? 0))
    if (sortBy === 'featured') list = [...list].sort((a, b) => b.featured - a.featured || a.sort - b.sort)
    return list
  }, [products, category, active, sub, sortBy, q])

  // Subcategories are free text, so "Sliding System", "Sliding Systems" and a stray
  // leading space used to render as three separate chips. Fold them together on the way
  // out and keep the first spelling seen as the label.
  const subcats = useMemo(() => {
    if (!products || !active) return []
    const seen = new Map()
    products.filter((p) => isInCategory(p, category)).forEach((p) => {
      const label = String(p.subcategory || '').trim()
      if (label && !seen.has(subKey(label))) seen.set(subKey(label), label)
    })
    return [...seen.values()]
  }, [products, category, active])

  return (
    <main className="container page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link>
        {active && <> / <span>{active.name}</span></>}
      </nav>
      <header className="page-head">
        <h1>{active ? active.name : 'All Products'}</h1>
        <p>{active ? active.blurb : 'Everything you need for a premium glass bathroom — screens, panels, hardware and tools.'}</p>
      </header>

      <div className="shop-toolbar">
        <div className="shop-toolbar-controls">
          <label className="shop-search">
            <Icon name="search" size={16} />
            <input
              value={q}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products…"
              aria-label="Search products"
            />
            {q && (
              <button type="button" className="shop-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                <Icon name="x" size={14} />
              </button>
            )}
          </label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort products">
            <option value="featured">Sort: Featured</option>
            <option value="price-asc">Price: low → high</option>
            <option value="price-desc">Price: high → low</option>
          </select>
        </div>
        <div className="chip-row" role="tablist" aria-label="Category" ref={(el) => { chipRows.current[0] = el }}>
          <Link to="/shop" className={`chip ${!active ? 'chip-active' : ''}`}>All</Link>
          {CATEGORIES.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} className={`chip ${category === c.slug ? 'chip-active' : ''}`}>
              {c.name}
            </Link>
          ))}
        </div>
      </div>

      {category === 'hardware' && (
        <section className="finish-strip">
          <h2>Shop hardware by finish</h2>
          <p className="muted">
            Every hinge, clamp, bracket, knob and channel comes in these six finishes. Pick
            yours to see the full price list with quantities you can add straight to the cart.
          </p>
          <FinishGrid headingLevel="h3" />
        </section>
      )}

      {subcats.length > 1 && (
        <div className="chip-row chip-row-sub" ref={(el) => { chipRows.current[1] = el }}>
          <button className={`chip ${!sub ? 'chip-active' : ''}`} onClick={() => setSub('')}>All {active.name}</button>
          {subcats.map((s) => (
            <button key={s} className={`chip ${sub === s ? 'chip-active' : ''}`} onClick={() => setSub(s)}>{s}</button>
          ))}
        </div>
      )}

      {products === null ? (
        <p className="muted">Loading products…</p>
      ) : filtered.length === 0 ? (
        q.trim() ? (
          <div className="empty-state">
            <p>No products match &ldquo;{q.trim()}&rdquo;{active ? ` in ${active.name}` : ''}.</p>
            <button className="btn btn-outline" onClick={() => setQuery('')}>Clear search</button>
          </div>
        ) : (
          <p className="muted">No products found in this category yet.</p>
        )
      ) : (
        <div className="product-grid">
          {filtered.map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      )}
    </main>
  )
}
